import { Injectable } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import * as fromStore from '@core/store/reducers';
import { DistributedDocumentsAdapter } from '@core/adapters/distributed-documents.adapter';
import { DistributedDocumentsResponse } from '../models/api/response/distributed-documents-response.model';
import { Store } from '@ngrx/store';
import { PersonaSelectors, QuoteLetterSelectors } from '../store/selectors';
import { catchError, take } from 'rxjs/operators';
import { Base64Utils } from '@shared/utils/base64.utils';
import { NavigationService } from './navigation.service';
import { HttpClient } from '@angular/common/http';
import {
  PersonaReport,
  PersonaNameToSupplementPdfPath,
  PersonaName,
} from '@entities/persona/persona.model';
import { LogService } from './log.service';
import { ProductsService } from './products.service';
import { Nullable } from '@shared/utils/type.utils';

@Injectable({
  providedIn: 'root',
})
export class QuoteLetterService {
  constructor(
    private distributedDocumentsAdapter: DistributedDocumentsAdapter,
    private store: Store<fromStore.CoreState>,
    private navigationService: NavigationService,
    private base64Utils: Base64Utils,
    private httpClient: HttpClient,
    private logService: LogService,
    private productService: ProductsService,
  ) {}

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  documents(request: any): Observable<DistributedDocumentsResponse> {
    return this.distributedDocumentsAdapter.documents(request);
  }

  getQuoteLetter(): Observable<string> {
    return this.store.select(QuoteLetterSelectors.getQuoteLetter);
  }

  getPersonaReport(): Observable<PersonaReport> {
    return this.store.select(PersonaSelectors.getPersonaReport);
  }

  showQuoteLetterInNewTab(): void {
    combineLatest([this.getQuoteLetter(), this.getPersonaReport(), this.productService.getSelectedProductTypes()])
      .pipe(take(1))
      .subscribe(([letter, personaReport, productTypes]) =>
        this.showEncodedQuoteLetterInNewTab(letter, personaReport.name, productTypes)
      );
  }

  showEncodedQuoteLetterInNewTab(
    base64EncodedPdf: string,
    personaName: PersonaName,
    productTypes: string[]
  ): void {
    if (!base64EncodedPdf) {
      return;
    }
    this.getPersonaSupplementPdfBlob(personaName, productTypes).subscribe(
      (supplementBlob) => {
        this.logService.logBusinessEvent('show-quote-pdf', {
          personaName,
          personaSupplement: !!supplementBlob.size,
        });
        this.base64Utils
          .decodeBase64ToBlobUrl(
            base64EncodedPdf,
            'application/pdf',
            supplementBlob
          )
          .then((url) => {
            this.navigationService.openExternalPage(url, '_blank');
          })
          .catch((err) => {
            // Opportunity here to report the error.
          });
      }
    );
  }

  getPersonaPdfPath(
    personaName: PersonaName,
    hasAuto: boolean,
  ): Nullable<string> {
    const pdf = PersonaNameToSupplementPdfPath[personaName];
    return typeof pdf === 'object' && pdf !== null
      ? hasAuto
        ? pdf.multiline
        : pdf.monoline
      : (pdf as Nullable<string>);
  }

  getPersonaSupplementPdfBlob(personaName: PersonaName, productTypes: string[]): Observable<Blob> {
    let supplementBlob: Blob = new Blob();
    const pdfPath = this.getPersonaPdfPath(personaName, productTypes.includes('PersonalAuto'));
    if (!!pdfPath) {
      return this.httpClient.get(pdfPath, { responseType: 'blob' }).pipe(
        take(1),
        catchError(() => from([]))
      );
    } else {
      return of(supplementBlob);
    }
  }
}
